import React from 'react';
import { Code, Palette, Server, Wrench, Smartphone, Laptop, Headphones, Cloud, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { services } from '../data/mock';

const iconMap = {
  Code,
  Palette,
  Server,
  Wrench,
  Smartphone,
  Laptop,
  Headphones,
  Cloud
};

const Services = () => {
  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-50 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-blue-500 font-bold text-sm uppercase tracking-wider bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
              What We Offer
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
            Our <span className="text-blue-500">Services</span>
          </h2>
          <p className="text-xl text-slate-600">
            Comprehensive IT solutions tailored to your business needs. From design to deployment and beyond.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const isComingSoon = service.status === 'coming-soon';

            return (
              <Card
                key={service.id}
                className={`group relative overflow-hidden border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${
                  isComingSoon ? 'border-slate-200 bg-slate-50' : 'border-slate-200 hover:border-blue-300 bg-white'
                }`}
              >
                <CardContent className="p-6">
                  {/* Status Badge */}
                  {isComingSoon && (
                    <Badge className="absolute top-4 right-4 bg-slate-200 text-slate-600 border-0 font-semibold">
                      Coming Soon
                    </Badge>
                  )}

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
                    isComingSoon
                      ? 'bg-slate-200 text-slate-400'
                      : 'bg-blue-500 text-white group-hover:scale-110 group-hover:rotate-6'
                  }`}>
                    <Icon className="w-8 h-8" strokeWidth={2.5} />
                  </div>

                  {/* Title */}
                  <h3 className={`text-xl font-bold mb-3 ${
                    isComingSoon ? 'text-slate-500' : 'text-slate-900 group-hover:text-blue-500'
                  } transition-colors duration-300`}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className={`leading-relaxed text-sm ${
                    isComingSoon ? 'text-slate-400' : 'text-slate-600'
                  }`}>
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h3 className="text-3xl font-black mb-4">Need a Custom Solution?</h3>
          <p className="text-xl mb-6 max-w-2xl mx-auto opacity-95">
            Don't see what you need? We're constantly expanding our services and can create tailored solutions for your unique requirements.
          </p>
          <a
            href="#contact"
            className="inline-block bg-white text-blue-500 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            Contact Us Today
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;